import { SongPreview } from '~/interfaces';
import RoomStateStore, {
  chatMessageReceived,
  countdownStarted,
  kicked,
  playbackStartSignal,
  playersChanged,
  roomError,
  showResults,
  songSelectedByHost,
} from './RoomState';
import SocketClient from './SocketClient';
import type { RoomData } from './messages';

class MultiplayerManager {
  private initialized = false;
  private lastRoomCode: string | null = null;
  private lastPlayerName: string | null = null;
  private rejoinSetup = false;

  async createRoom(playerName: string): Promise<string> {
    await SocketClient.connect();
    this.setupListeners();

    const code = await new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout creating room'));
      }, 10000);

      const onCreated = (data: { code: string; playerId: string; room: RoomData }) => {
        cleanup();
        RoomStateStore.setRoom(data.room, data.playerId);
        resolve(data.code);
      };

      const onError = (data: { message: string }) => {
        cleanup();
        reject(new Error(data.message));
      };

      const cleanup = () => {
        clearTimeout(timeout);
        SocketClient.off('room:created', onCreated);
        SocketClient.off('room:error', onError);
      };

      SocketClient.on('room:created', onCreated);
      SocketClient.on('room:error', onError);
      SocketClient.emit('room:create', { name: playerName });
    });

    this.lastRoomCode = code;
    this.lastPlayerName = playerName;
    return code;
  }

  async joinRoom(code: string, playerName: string): Promise<void> {
    await SocketClient.connect();
    this.setupListeners();

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout joining room'));
      }, 10000);

      const onJoined = (data: { playerId: string; room: RoomData }) => {
        cleanup();
        RoomStateStore.setRoom(data.room, data.playerId);
        resolve();
      };

      const onError = (data: { message: string }) => {
        cleanup();
        reject(new Error(data.message));
      };

      const cleanup = () => {
        clearTimeout(timeout);
        SocketClient.off('room:joined', onJoined);
        SocketClient.off('room:error', onError);
      };

      SocketClient.on('room:joined', onJoined);
      SocketClient.on('room:error', onError);
      SocketClient.emit('room:join', { code, name: playerName });
    });

    this.lastRoomCode = code;
    this.lastPlayerName = playerName;
  }

  leaveRoom(): void {
    SocketClient.emit('room:leave');
    RoomStateStore.setReconnecting(false);
    RoomStateStore.clear();
    SocketClient.disconnect();
    this.initialized = false;
    this.rejoinSetup = false;
    this.lastRoomCode = null;
    this.lastPlayerName = null;
  }

  // Host-only actions
  selectSong(song: SongPreview): void {
    SocketClient.emit('game:select-song', { song });
  }

  queueSong(song: SongPreview): void {
    SocketClient.emit('game:queue-song', { song });
  }

  removeQueued(index: number): void {
    SocketClient.emit('game:remove-queued', { index });
  }

  startCountdown(tolerance: number): void {
    SocketClient.emit('game:start-countdown', { tolerance });
  }

  kickPlayer(targetId: string): void {
    SocketClient.emit('room:kick', { targetId });
  }

  nextSong(): void {
    SocketClient.emit('game:next-song');
  }

  backToLobby(): void {
    SocketClient.emit('game:back-to-lobby');
  }

  // All players
  reportReady(): void {
    SocketClient.emit('game:player-ready');
  }

  reportScore(score: number): void {
    SocketClient.emit('game:score-update', { score: Math.floor(score) });
  }

  reportSongEnded(finalScore: number): void {
    SocketClient.emit('game:song-ended', { finalScore: Math.floor(finalScore) });
  }

  updateName(name: string): void {
    SocketClient.emit('player:update-name', { name });
  }

  sendChat(text: string): void {
    SocketClient.emit('chat:message', { text });
  }

  // Getters
  isInRoom(): boolean {
    return RoomStateStore.isInRoom;
  }

  isHost(): boolean {
    return RoomStateStore.isHost;
  }

  getRoom() {
    return RoomStateStore.room;
  }

  private setupListeners(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Player events
    SocketClient.on('player:joined', (data: any) => {
      RoomStateStore.updatePlayers(data.players);
    });

    SocketClient.on('player:left', (data: any) => {
      RoomStateStore.updatePlayers(data.players);
    });

    SocketClient.on('player:disconnected', (data: any) => {
      RoomStateStore.updatePlayers(data.players);
    });

    SocketClient.on('player:updated', (data: any) => {
      RoomStateStore.updatePlayers(data.players);
    });

    SocketClient.on('room:host-changed', (data: any) => {
      RoomStateStore.updatePlayers(data.players);
    });

    // Game events
    SocketClient.on('game:song-selected', (data: any) => {
      // Update song + state atomically — a single dispatch prevents handleRoomChange
      // from seeing an intermediate LOBBY state while song is set but state isn't yet.
      RoomStateStore.setSongAndState(data.song, data.state, data.songQueue);
      songSelectedByHost.dispatch(data.song);
    });

    SocketClient.on('game:queue-updated', (data: any) => {
      RoomStateStore.updateQueue(data.songQueue);
    });

    SocketClient.on('game:countdown-started', (data: any) => {
      RoomStateStore.updateState(data.state);
      if (typeof data.tolerance === 'number') {
        RoomStateStore.updateTolerance(data.tolerance);
      }
      countdownStarted.dispatch(data.song, data.tolerance ?? 2);
    });

    SocketClient.on('game:player-ready-update', (data: any) => {
      RoomStateStore.updatePlayers(data.players);
      playersChanged.dispatch(data.players);
    });

    SocketClient.on('game:start-playback', (data: any) => {
      RoomStateStore.updateState(data.state);
      playbackStartSignal.dispatch(data.startTimestamp);
    });

    SocketClient.on('game:scores-update', (data: any) => {
      RoomStateStore.updateScores(data.scores);
    });

    SocketClient.on('game:show-results', (data: any) => {
      RoomStateStore.updateState(data.state);
      RoomStateStore.updateScores(data.scores);
      showResults.dispatch(data.scores);
    });

    SocketClient.on('game:state-changed', (data: any) => {
      RoomStateStore.updateState(data.state);
      if (data.room) {
        RoomStateStore.setRoom(data.room, RoomStateStore.myPlayerId!);
      }
    });

    // Room events
    SocketClient.on('room:kicked', () => {
      RoomStateStore.clear();
      kicked.dispatch();
    });

    SocketClient.on('room:expired', () => {
      RoomStateStore.clear();
      roomError.dispatch('Room expired due to inactivity');
    });

    SocketClient.on('room:error', (data: any) => {
      roomError.dispatch(data.message);
    });

    SocketClient.on('chat:message', (data: any) => {
      chatMessageReceived.dispatch(data);
    });

    // Handle unexpected disconnects - show reconnecting state instead of clearing immediately
    SocketClient.on('disconnect', () => {
      if (RoomStateStore.isInRoom) {
        RoomStateStore.setReconnecting(true);
      }
      this.initialized = false;
    });

    // Set up reconnection handler
    if (!this.rejoinSetup) {
      this.rejoinSetup = true;
      SocketClient.onReconnect = () => {
        if (!RoomStateStore.isInRoom || !this.lastRoomCode || !this.lastPlayerName) {
          RoomStateStore.setReconnecting(false);
          return;
        }
        this.setupListeners();
        const previousId = SocketClient.previousId;

        const timeout = setTimeout(() => {
          cleanup();
          RoomStateStore.setReconnecting(false);
          RoomStateStore.clear();
          roomError.dispatch('Nie udało się ponownie połączyć');
        }, 10000);

        const onRejoined = (data: { playerId: string; room: RoomData }) => {
          cleanup();
          RoomStateStore.setReconnecting(false);
          RoomStateStore.setRoom(data.room, data.playerId);
        };

        const onRejoinFailed = () => {
          cleanup();
          RoomStateStore.setReconnecting(false);
          RoomStateStore.clear();
          roomError.dispatch('Pokój nie istnieje lub gra w trakcie');
        };

        const cleanup = () => {
          clearTimeout(timeout);
          SocketClient.off('room:rejoined', onRejoined);
          SocketClient.off('room:rejoin-failed', onRejoinFailed);
        };

        SocketClient.on('room:rejoined', onRejoined);
        SocketClient.on('room:rejoin-failed', onRejoinFailed);
        SocketClient.emit('room:rejoin', {
          code: this.lastRoomCode!,
          name: this.lastPlayerName!,
          previousId: previousId || '',
        });
      };
    }
  }
}

export default new MultiplayerManager();
